import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { View } from 'react-big-calendar';
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_NAV_PREV,
  CALENDAR_NAV_TODAY,
  CALENDAR_VIEW_MONTH,
  CALENDAR_VIEW_WEEK,
  CALENDAR_VIEWS,
  CalendarNavAction,
  DEFAULT_CALENDAR_VIEW,
} from 'dashboard409a/common/constants/calendar';

interface UseCalendarStateProps {
  dateStorageKey: string;
  viewStorageKey: string;
}

interface UseCalendarStateReturn {
  calendarDate: Date;
  setCalendarDate: (date: Date) => void;
  calendarView: View;
  handleCalendarNavigate: (action: CalendarNavAction) => void;
  handleCalendarViewChange: (view: View) => void;
}

export const useCalendarState = ({ dateStorageKey, viewStorageKey }: UseCalendarStateProps): UseCalendarStateReturn => {
  const [calendarDate, setCalendarDate] = useState<Date>(() => {
    if (globalThis.window) {
      const savedDate = globalThis.window.localStorage.getItem(dateStorageKey);
      if (savedDate) {
        const date = moment(savedDate, CALENDAR_DATE_FORMAT);
        if (date.isValid()) {
          return date.toDate();
        }
      }
    }
    return new Date();
  });

  const [calendarView, setCalendarView] = useState<View>(() => {
    if (globalThis.window) {
      const savedView = globalThis.window.localStorage.getItem(viewStorageKey) as View;
      if (savedView && CALENDAR_VIEWS.includes(savedView as typeof CALENDAR_VIEWS[number])) {
        return savedView;
      }
    }
    return DEFAULT_CALENDAR_VIEW;
  });

  useEffect(() => {
    if (globalThis.window) {
      globalThis.window.localStorage.setItem(dateStorageKey, moment(calendarDate).format(CALENDAR_DATE_FORMAT));
    }
  }, [calendarDate, dateStorageKey]);

  useEffect(() => {
    if (globalThis.window) {
      if (calendarView === DEFAULT_CALENDAR_VIEW) {
        globalThis.window.localStorage.removeItem(viewStorageKey);
      } else {
        globalThis.window.localStorage.setItem(viewStorageKey, calendarView);
      }
    }
  }, [calendarView, viewStorageKey]);

  const handleCalendarNavigate = useCallback((action: CalendarNavAction) => {
    setCalendarDate(prevDate => {
      if (action === CALENDAR_NAV_TODAY) {
        return new Date();
      }
      const unit = calendarView === CALENDAR_VIEW_MONTH ? CALENDAR_VIEW_MONTH : CALENDAR_VIEW_WEEK;
      if (action === CALENDAR_NAV_PREV) {
        return moment(prevDate).subtract(1, unit).toDate();
      }
      return moment(prevDate).add(1, unit).toDate();
    });
  }, [calendarView]);

  const handleCalendarViewChange = useCallback((view: View) => {
    setCalendarView(view);
  }, []);

  return {
    calendarDate,
    setCalendarDate,
    calendarView,
    handleCalendarNavigate,
    handleCalendarViewChange,
  };
};
